import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { Box, IconButton, Typography } from "@mui/material";
import type { AnalyzedMoveResult } from "@/shared/api/engine.api";

type RailMove = Pick<
  AnalyzedMoveResult,
  "moveNumber" | "san" | "isPlayerMove" | "centipawnLoss" | "isBlunder" | "isMistake" | "isImprecision" | "bestMoveSan"
>;

const classification = (move: RailMove): { label: string; color: string } =>
  move.isBlunder
    ? { label: "BLUNDER", color: "error.main" }
    : move.isMistake
      ? { label: "MISTAKE", color: "secondary.main" }
      : move.isImprecision
        ? { label: "IMPRECISION", color: "#d9a441" }
        : move.bestMoveSan === move.san
          ? { label: "BEST LINE", color: "primary.main" }
          : { label: "PLAYED", color: "text.secondary" };

const pawnLoss = (centipawns: number): string => {
  const pawns = Math.max(0, centipawns) / 100;
  return pawns === 0 ? "0.00" : `−${pawns.toFixed(pawns >= 10 ? 1 : 2)}`;
};

export const InlineMoveRail = ({
  move,
  ply,
  totalPlies,
  canGoBack,
  canGoForward,
  onFirst,
  onPrevious,
  onNext,
  onLast,
}: {
  move: RailMove;
  ply: number | null;
  totalPlies: number | null;
  canGoBack: boolean;
  canGoForward: boolean;
  onFirst: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onLast: () => void;
}) => {
  const quality = classification(move);
  const better = move.bestMoveSan && move.bestMoveSan !== move.san ? move.bestMoveSan : null;
  return (
    <Box
      data-testid="inline-move-rail"
      sx={{
        mt: 1,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "rgba(240,236,226,.025)",
        backgroundImage: "linear-gradient(135deg,rgba(255,61,18,.08),transparent 48%)",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr auto", sm: "minmax(0,1fr) minmax(9rem,.8fr) auto" },
          alignItems: "stretch",
        }}
      >
        <Box sx={{ minWidth: 0, p: { xs: 1.15, sm: 1.4 } }}>
          <Typography sx={{ color: "text.secondary", fontSize: ".58rem", fontWeight: 900, letterSpacing: ".11em" }}>
            MOVE {move.moveNumber} · {move.isPlayerMove ? "YOUR MOVE" : "OPPONENT MOVE"}
          </Typography>
          <Typography
            sx={{ mt: 0.25, fontSize: "clamp(1.25rem,4vw,1.75rem)", fontWeight: 950, letterSpacing: "-.07em" }}
          >
            {move.san}
          </Typography>
        </Box>
        <Box
          sx={{
            display: { xs: "none", sm: "block" },
            minWidth: 0,
            p: 1.4,
            borderInlineStart: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: ".56rem", fontWeight: 900, letterSpacing: ".11em" }}>
            {better ? "BETTER IDEA" : "ENGINE VERDICT"}
          </Typography>
          <Typography
            sx={{ mt: 0.35, color: better ? "primary.main" : quality.color, fontSize: ".9rem", fontWeight: 900 }}
          >
            {better ?? quality.label}
          </Typography>
        </Box>
        <Box
          sx={{ p: { xs: 1.15, sm: 1.4 }, textAlign: "right", borderInlineStart: "1px solid", borderColor: "divider" }}
        >
          <Typography sx={{ color: quality.color, fontSize: ".56rem", fontWeight: 950, letterSpacing: ".1em" }}>
            {quality.label}
          </Typography>
          <Typography sx={{ mt: 0.35, fontSize: "1rem", fontWeight: 950 }}>{pawnLoss(move.centipawnLoss)}</Typography>
          <Typography sx={{ color: "text.secondary", fontSize: ".5rem", fontWeight: 800, letterSpacing: ".08em" }}>
            EVAL SWING
          </Typography>
        </Box>
      </Box>
      {better ? (
        <Box
          sx={{
            display: { xs: "flex", sm: "none" },
            gap: 1,
            px: 1.15,
            py: 0.8,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography sx={{ color: "text.secondary", fontSize: ".56rem", fontWeight: 900, letterSpacing: ".1em" }}>
            BETTER IDEA
          </Typography>
          <Typography sx={{ color: "primary.main", fontSize: ".72rem", fontWeight: 950 }}>{better}</Typography>
        </Box>
      ) : null}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "44px 44px 1fr 44px 44px",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <IconButton aria-label="First ply" disabled={!canGoBack} onClick={onFirst} sx={{ borderRadius: 0 }}>
          <FirstPageIcon fontSize="small" />
        </IconButton>
        <IconButton aria-label="Previous ply" disabled={!canGoBack} onClick={onPrevious} sx={{ borderRadius: 0 }}>
          <KeyboardArrowLeftIcon />
        </IconButton>
        <Typography
          sx={{
            display: "grid",
            placeItems: "center",
            borderInline: "1px solid",
            borderColor: "divider",
            color: "text.secondary",
            fontSize: ".62rem",
            fontWeight: 900,
            letterSpacing: ".08em",
          }}
        >
          PLY {ply ?? "—"} / {totalPlies ?? "—"}
        </Typography>
        <IconButton aria-label="Next ply" disabled={!canGoForward} onClick={onNext} sx={{ borderRadius: 0 }}>
          <KeyboardArrowRightIcon />
        </IconButton>
        <IconButton aria-label="Last ply" disabled={!canGoForward} onClick={onLast} sx={{ borderRadius: 0 }}>
          <LastPageIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
